/**
 * AdminAnsellDatasheets — mirror PDF datasheets locally so buyers keep
 * getting them even when Ansell rotates URLs.  Route: /admin/ansell-datasheets
 *
 * Access: super_admin / hub_admin.  Uploads go through the backend at
 *   POST /api/gloves/ansell/datasheets/{slug}?label=<label>
 * (multipart PDF).  Deletes hit DELETE /api/gloves/ansell/datasheets/{slug}/{file}.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { FileText, Upload, Check, X, AlertTriangle, Archive } from "lucide-react";

export default function AdminAnsellDatasheets() {
  const { token } = useAuth();
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [filter, setFilter] = useState("all");
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const reload = () => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/gloves/ansell/datasheets-manifest`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { setManifest(r.data); setLoading(false); })
      .catch((err) => {
        setError(err.response?.status === 403 || err.response?.status === 401
          ? "Access denied — restricted to super_admin / hub_admin."
          : "Failed to load datasheet manifest.");
        setLoading(false);
      });
  };
  useEffect(reload, [token]);

  const rows = useMemo(() => {
    if (!manifest) return [];
    let list = manifest.products;
    if (filter === "gap") list = list.filter((p) => p.mirrored.length === 0);
    if (filter === "done") list = list.filter((p) => p.mirrored.length > 0);
    return list;
  }, [manifest, filter]);

  async function bulkUpload(zipFile) {
    if (!zipFile) return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const form = new FormData();
      form.append("file", zipFile);
      const r = await axios.post(
        `${API}/gloves/ansell/datasheets-bulk-upload`,
        form,
        {
          headers: { Authorization: `Bearer ${token}` },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        },
      );
      setBulkResult(r.data);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulkBusy(false);
    }
  }

  async function upload(slug, label, file) {
    if (!file) return;
    setBusy((b) => ({ ...b, [slug]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(
        `${API}/gloves/ansell/datasheets/${slug}?label=${encodeURIComponent(label)}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy((b) => ({ ...b, [slug]: false }));
    }
  }

  async function remove(slug, filename) {
    if (!window.confirm(`Delete ${filename}?`)) return;
    try {
      await axios.delete(`${API}/gloves/ansell/datasheets/${slug}/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    }
  }

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (error) return <div className="p-8 text-red-600 flex items-center gap-2"><AlertTriangle className="w-5 h-5"/>{error}</div>;

  const pct = manifest.specs_products
    ? Math.round((manifest.products_with_local_pdfs / manifest.specs_products) * 100)
    : 0;

  return (
    <div className="p-6 space-y-6" data-testid="admin-ansell-datasheets">
      <header>
        <p className="text-[11px] font-black uppercase tracking-widest text-blue-600">Admin · Ansell</p>
        <h1 className="text-2xl font-black text-slate-900 mt-1">Datasheet Mirror</h1>
        <p className="text-sm text-slate-600 mt-1">
          Host PDFs on sterilemate.com so buyers keep getting them even when Ansell rotates URLs.
          Uploads land in <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">/api/static/ansell_datasheets/&lt;slug&gt;/</code>.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-white border border-slate-200">
          <p className="text-[11px] font-black uppercase text-slate-500">Products With Specs</p>
          <p className="text-3xl font-black text-slate-900 mt-1">{manifest.specs_products}</p>
        </div>
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
          <p className="text-[11px] font-black uppercase text-emerald-700">Mirrored (has PDFs)</p>
          <p className="text-3xl font-black text-emerald-800 mt-1">
            {manifest.products_with_local_pdfs} <span className="text-base text-emerald-700">({pct}%)</span>
          </p>
        </div>
        <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
          <p className="text-[11px] font-black uppercase text-blue-700">Total PDFs Hosted</p>
          <p className="text-3xl font-black text-blue-800 mt-1">{manifest.total_pdfs}</p>
        </div>
      </section>

      {/* BULK ZIP UPLOAD */}
      <section
        className="rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/40 p-6"
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-blue-100"); }}
        onDragLeave={(e) => { e.currentTarget.classList.remove("bg-blue-100"); }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove("bg-blue-100");
          const f = e.dataTransfer.files[0];
          if (f && (f.name.toLowerCase().endsWith(".zip") || f.type === "application/zip")) {
            bulkUpload(f);
          } else {
            alert("Please drop a ZIP file containing PDFs.");
          }
        }}
        data-testid="ansell-ds-bulk-dropzone"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0">
            <Archive className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h2 className="font-black text-slate-900">Bulk Upload · ZIP from Ansell Partner Portal</h2>
            <p className="text-sm text-slate-600 mt-1">
              Drop a ZIP full of Ansell PDFs. We auto-route each file to the right product by matching the filename
              against product slugs, codes (<code className="bg-white px-1 rounded text-xs">11-840</code>), MRPNs and SKUs.
              Anything unmatched appears below for manual placement.
            </p>
            <div className="mt-3 flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold">
                <Upload className="w-4 h-4" />
                {bulkBusy ? "Uploading…" : "Choose ZIP"}
                <input
                  type="file"
                  accept=".zip,application/zip"
                  className="hidden"
                  disabled={bulkBusy}
                  data-testid="ansell-ds-bulk-file"
                  onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; bulkUpload(f); }}
                />
              </label>
              <span className="text-xs text-slate-500">…or drag & drop the ZIP anywhere on this box.</span>
            </div>
            {bulkResult && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                <div className="p-2 rounded bg-emerald-100 text-emerald-900">
                  <p className="font-black">Matched: {bulkResult.matched.length}</p>
                  {bulkResult.matched.slice(0, 5).map((m, i) => (
                    <p key={i} className="truncate">→ {m.slug}</p>
                  ))}
                  {bulkResult.matched.length > 5 && (
                    <p className="text-emerald-700">+ {bulkResult.matched.length - 5} more</p>
                  )}
                </div>
                <div className="p-2 rounded bg-amber-100 text-amber-900">
                  <p className="font-black">Unmatched: {bulkResult.unmatched.length}</p>
                  {bulkResult.unmatched.slice(0, 5).map((m, i) => (
                    <p key={i} className="truncate">? {m.file}</p>
                  ))}
                  {bulkResult.unmatched.length > 5 && (
                    <p className="text-amber-700">+ {bulkResult.unmatched.length - 5} more</p>
                  )}
                </div>
                <div className="p-2 rounded bg-slate-100 text-slate-800">
                  <p className="font-black">Skipped: {bulkResult.skipped.length}</p>
                  <p className="text-[10px] text-slate-600">non-PDF entries</p>
                </div>
                <div className="p-2 rounded bg-red-100 text-red-900">
                  <p className="font-black">Errors: {bulkResult.errors.length}</p>
                  {bulkResult.errors.slice(0, 3).map((m, i) => (
                    <p key={i} className="truncate">{m.reason}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="flex items-center gap-2">
        {["all", "gap", "done"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`ansell-ds-filter-${f}`}
            className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${
              filter === f
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
            }`}
          >
            {f === "all" ? "All" : f === "gap" ? "Missing PDFs" : "Mirrored"}
          </button>
        ))}
        <span className="text-xs text-slate-500 ml-auto">{rows.length} shown</span>
      </div>

      <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-black">Product</th>
              <th className="text-left px-4 py-3 font-black">Mirrored PDFs</th>
              <th className="text-left px-4 py-3 font-black w-96">Add PDF</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <ProductRow
                key={p.slug}
                product={p}
                busy={busy[p.slug]}
                onUpload={upload}
                onRemove={remove}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductRow({ product, busy, onUpload, onRemove }) {
  const [label, setLabel] = useState("Product Data Sheet");
  const [file, setFile] = useState(null);
  const has = product.mirrored.length > 0;

  return (
    <tr className="border-t border-slate-100" data-testid={`ansell-ds-row-${product.slug}`}>
      <td className="px-4 py-3 align-top">
        <p className="font-bold text-slate-900">{product.slug.replace(/-/g, " ").toUpperCase()}</p>
        <p className="text-xs text-slate-500 mt-0.5">{product.slug}</p>
        {product.expected?.length > 0 && (
          <p className="text-[11px] text-slate-400 mt-1">
            Ansell lists: {product.expected.join(", ")}
          </p>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        {has ? (
          <ul className="space-y-1">
            {product.mirrored.map((f) => (
              <li key={f} className="flex items-center gap-2 text-xs">
                <FileText className="w-3.5 h-3.5 text-emerald-600" />
                <a
                  href={`${API}/gloves/ansell/datasheet/${product.slug}/${f}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-700 hover:underline"
                >{f}</a>
                <button
                  onClick={() => onRemove(product.slug, f)}
                  data-testid={`ansell-ds-delete-${product.slug}-${f}`}
                  title="Delete"
                  className="text-slate-400 hover:text-red-600"
                ><X className="w-3.5 h-3.5" /></button>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-xs text-slate-400">— none —</span>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (e.g. Product Data Sheet)"
            className="flex-1 px-2 py-1.5 text-xs border border-slate-300 rounded"
            data-testid={`ansell-ds-label-${product.slug}`}
          />
          <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1.5 text-xs font-bold border border-slate-300 rounded hover:bg-slate-50">
            <Upload className="w-3.5 h-3.5" />
            {file ? file.name.slice(0, 12) : "PDF"}
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0])}
              data-testid={`ansell-ds-file-${product.slug}`}
            />
          </label>
          <button
            disabled={!file || busy}
            onClick={() => { onUpload(product.slug, label, file); setFile(null); }}
            data-testid={`ansell-ds-upload-${product.slug}`}
            className="px-3 py-1.5 text-xs font-bold rounded bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white inline-flex items-center gap-1"
          >
            {busy ? "…" : <><Check className="w-3.5 h-3.5" /> Save</>}
          </button>
        </div>
      </td>
    </tr>
  );
}
